import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { PostCard } from './components/PostCard';
import type { SocialPost, SocialPostType } from './types';
import {
  Search,
  ArrowLeft,
  X,
  Loader2,
  Filter,
  Sparkles,
  RotateCcw,
  FileText,
  Clock,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCommunityContext } from '@/providers/community-context';
import { cn } from '@/lib/utils';

const POST_TYPES: { label: string; value: SocialPostType | 'ALL' }[] = [
  { label: 'All Types', value: 'ALL' },
  { label: 'General', value: 'GENERAL' },
  { label: 'Leads', value: 'LEAD' },
  { label: 'Testimonials', value: 'TESTIMONIAL' },
  { label: 'Thank You', value: 'THANK_YOU' },
  { label: 'Meetups', value: 'MEETUP' },
  { label: 'Polls', value: 'POLL' },
];

type TimeframeOption = 'all' | 'today' | 'week' | 'month' | 'year';

const TIMEFRAME_OPTIONS: { label: string; value: TimeframeOption }[] = [
  { label: 'All Time', value: 'all' },
  { label: 'Today (24h)', value: 'today' },
  { label: 'Past 7 Days', value: 'week' },
  { label: 'Past 30 Days', value: 'month' },
  { label: 'Past Year', value: 'year' },
];

export function SocialSearchPage() {
  const navigate = useNavigate();
  const { communityId: routeCommunityId } = useParams<{ communityId: string }>();
  const { community: currentCommunity } = useCommunityContext();
  const communityId = routeCommunityId || currentCommunity?.id || '';

  const [keyword, setKeyword] = useState('');
  const [selectedType, setSelectedType] = useState<SocialPostType | 'ALL'>('ALL');
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>('all');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const triggerRef = useRef<HTMLDivElement>(null);

  const executeSearch = useCallback(async () => {
    if (!communityId) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (keyword.trim()) params.set('keyword', keyword.trim());
      if (selectedType !== 'ALL') params.set('type', selectedType);
      if (selectedTimeframe !== 'all') params.set('timeframe', selectedTimeframe);

      params.set('offset', '0');
      params.set('limit', '20');

      const response = await axiosInstance.get(
        `/api/communities/${communityId}/posts/search?${params.toString()}`
      );
      setPosts(response.data.posts || []);
      setNextOffset(response.data.nextOffset ?? null);
      setHasMore(Boolean(response.data.hasMore));
      setHasSearched(true);
    } catch (err) {
      console.error('Failed to search posts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [communityId, keyword, selectedType, selectedTimeframe]);

  const loadMorePosts = useCallback(async () => {
    if (!communityId || isLoadingMore || !hasMore || nextOffset === null) return;

    setIsLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (keyword.trim()) params.set('keyword', keyword.trim());
      if (selectedType !== 'ALL') params.set('type', selectedType);
      if (selectedTimeframe !== 'all') params.set('timeframe', selectedTimeframe);

      params.set('offset', String(nextOffset));
      params.set('limit', '20');

      const response = await axiosInstance.get(
        `/api/communities/${communityId}/posts/search?${params.toString()}`
      );
      setPosts((prev) => [...prev, ...(response.data.posts || [])]);
      setNextOffset(response.data.nextOffset ?? null);
      setHasMore(Boolean(response.data.hasMore));
    } catch (err) {
      console.error('Failed to load more search posts:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [communityId, keyword, selectedType, selectedTimeframe, isLoadingMore, hasMore, nextOffset]);

  // Debounced search trigger when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      executeSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [executeSearch]);

  // IntersectionObserver for infinite scrolling pagination
  useEffect(() => {
    if (!hasMore || isLoading || isLoadingMore || !triggerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore &&
          nextOffset !== null
        ) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(triggerRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, nextOffset, loadMorePosts]);

  const handlePostDelete = (postId: string) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  const handleClearFilters = () => {
    setKeyword('');
    setSelectedType('ALL');
    setSelectedTimeframe('all');
  };

  const activeSecondaryFiltersCount =
    (selectedType !== 'ALL' ? 1 : 0) + (selectedTimeframe !== 'all' ? 1 : 0);

  const hasActiveFilters = Boolean(
    keyword.trim() || selectedType !== 'ALL' || selectedTimeframe !== 'all'
  );

  return (
    <div className="max-w-4xl mx-auto space-y-4 pb-16 px-1 sm:px-3 md:px-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-3 py-2 px-1 border-b border-border/60">
        <div className="flex items-center gap-2.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/${communityId}/dashboard?view=social`)}
            className="rounded-full h-9 w-9 hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors shrink-0"
            title="Back to Social Dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight font-outfit">
              Search Social Feed
            </h1>
            <p className="text-[11px] sm:text-xs text-muted-foreground line-clamp-1">
              Search posts by author, keywords, or time range
            </p>
          </div>
        </div>

        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearFilters}
            className="rounded-full border-border text-xs gap-1.5 hover:border-primary/40 hover:text-primary shrink-0"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      {/* Search Controls Card */}
      <div className="bg-card border border-border rounded-2xl p-3 sm:p-5 shadow-sm space-y-3">
        {/* Top Control Bar: Keywords Input + Mobile Toggle */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Keyword Search Input */}
          <div className="flex-1 space-y-1">
            <label className="text-[11px] font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Search className="h-3.5 w-3.5 text-primary" />
              Keywords Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Search words, content, or author..."
                className="pl-9 pr-8 rounded-xl bg-background border-border text-xs sm:text-sm focus-visible:ring-primary/20"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded-full"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Mobile Filter Toggle Button (Shown on mobile screens < md) */}
          <div className="md:hidden flex justify-end pt-1">
            <button
              type="button"
              onClick={() => setIsMobileFiltersOpen((prev) => !prev)}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border border-border transition-colors w-full justify-center',
                isMobileFiltersOpen || activeSecondaryFiltersCount > 0
                  ? 'bg-primary/10 text-primary border-primary/30'
                  : 'bg-muted/40 text-muted-foreground hover:bg-muted'
              )}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>Filters</span>
              {activeSecondaryFiltersCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                  {activeSecondaryFiltersCount}
                </span>
              )}
              {isMobileFiltersOpen ? (
                <ChevronUp className="h-3.5 w-3.5 ml-auto" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 ml-auto" />
              )}
            </button>
          </div>
        </div>

        {/* Collapsible Filter Body for Mobile / Visible by Default on Desktop */}
        <div className={cn('space-y-3 pt-1', isMobileFiltersOpen ? 'block' : 'hidden md:block')}>
          {/* Desktop & Mobile Collapsible Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-border/40">
            {/* Timeframe Filter */}
            <div className="space-y-1.5 min-w-0">
              <label className="text-[11px] font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary" />
                Time Filter
              </label>
              <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 max-w-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {TIMEFRAME_OPTIONS.map((tf) => (
                  <button
                    key={tf.value}
                    type="button"
                    onClick={() => setSelectedTimeframe(tf.value)}
                    className={`shrink-0 whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      selectedTimeframe === tf.value
                        ? 'bg-primary text-white shadow-sm shadow-primary/30'
                        : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Post Type Filters */}
            <div className="space-y-1.5 min-w-0">
              <label className="text-[11px] font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-primary" />
                Filter by Post Type
              </label>
              <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 max-w-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {POST_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setSelectedType(type.value)}
                    className={`shrink-0 whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      selectedType === type.value
                        ? 'bg-primary text-white shadow-sm shadow-primary/30'
                        : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Results Summary */}
      <div className="flex items-center justify-between px-1">
        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          {isLoading
            ? 'Searching social posts...'
            : hasSearched
              ? `Found ${posts.length} ${posts.length === 1 ? 'post' : 'posts'}`
              : 'Showing recent posts'}
        </p>
      </div>

      {/* Search Results Feed */}
      <div className="space-y-4 sm:space-y-6">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 text-center space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary opacity-70" />
            <p className="text-xs text-muted-foreground">
              Searching for posts matching your query...
            </p>
          </div>
        ) : posts.length > 0 ? (
          <>
            {posts.map((post, index) => (
              <div key={post.id} ref={index === posts.length - 3 ? triggerRef : undefined}>
                <PostCard post={post} onDelete={handlePostDelete} communityId={communityId} />
              </div>
            ))}

            {isLoadingMore && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary opacity-70" />
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center bg-card border border-border rounded-2xl p-8 space-y-3 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <FileText className="h-6 w-6" />
            </div>
            <h3 className="font-bold text-base text-foreground font-outfit">No Posts Found</h3>
            <p className="text-xs text-muted-foreground max-w-sm">
              We couldn't find any social posts matching your search criteria. Try modifying your
              keywords or filters.
            </p>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearFilters}
                className="mt-2 rounded-full border-border text-xs"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
